import React from "react";
//#region types
import {
  FormFieldSelectorExtraProps,
  FormFieldWrapperProps,
  GenericFormField,
} from "models/propTypes/genericFormProps";
import { SelectionItem } from "models/utilTypes/selectionTypes";
//#endregion
//#region components
import FormControlLayout from "./rowLayouts/formControlLayout";
import MultipleSelection from "../selection/multipleSelection";
import FormFieldWrapper from "./formFieldWrapper";
import { Grid } from "@mui/material";
import { Field, FieldArray, FieldArrayRenderProps, FieldProps, FormikProps } from "formik";
//#endregion
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

function FormFieldSelector<T extends Record<string, any>>(props: FormFieldWrapperProps<T>) {
  const fields = props.field.innerFields ?? ({} as Partial<Record<keyof T, GenericFormField<T>>>);
  const extraProps = props.field.extraProps as FormFieldSelectorExtraProps;

  return (
    <>
      <FormControlLayout
        names={[props.name]}
        label={props.field.label}
        descriptionText={props.field.descriptionText}
        formGridLayout={props.formGridLayout}
        inline={props.inline}
        enableControlLables={props.enableControlLables}
      >
        <Grid container direction="row" spacing={1}>
          <Field name={props.name}>
            {(fieldProps: FieldProps) => {
              const { value } = fieldProps.meta;
              const { setFieldValue, values } = fieldProps.form;

              const onChange = (
                items: SelectionItem[],
                arrayHelpers: FieldArrayRenderProps,
                formikProps: FormikProps<any>
              ) => {
                const formItems = items.map((i) => {
                  // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
                  const exists = value?.find((v: any) => v.id == i.id);
                  return exists ?? { id: i.id, name: i.value };
                });
                setFieldValue(props.name, formItems);
                props.field.onChangeHandler && props.field.onChangeHandler(props.name, formItems, fieldProps.form);
              };

              return (
                <FieldArray
                  name={props.name}
                  render={(arrayHelpers) =>
                    extraProps.selectionItems ? (
                      <MultipleSelection
                        items={extraProps.selectionItems(values).map((item: any, index: number) => {
                          return {
                            id: item.id,
                            value: item.value,
                            selected: item.selected,
                            data: item,
                          } as SelectionItem;
                        })}
                        onChange={(items: SelectionItem[]) => onChange(items, arrayHelpers, fieldProps.form)}
                      ></MultipleSelection>
                    ) : (
                      <></>
                    )
                  }
                />
              );
            }}
          </Field>
        </Grid>
      </FormControlLayout>

      <Field name={props.name}>
        {(fieldProps: FieldProps) => {
          const { value } = fieldProps.meta;

          return value ? (
            value.map((item: any, index: number) => (
              <FormControlLayout
                names={Object.keys(fields)}
                label={item.name}
                formGridLayout={props.formGridLayout}
                inline={props.inline}
                enableControlLables={props.enableControlLables}
                labelHorizontalAlignment={"flex-end"}
              >
                <Grid container direction="row" spacing={1}>
                  {Object.entries(fields).map(([key, field]) =>
                    field ? (
                      <>
                        <Grid key={`formFieldSelector-${index}-${key}`} item xs={field.widthColumnCount ?? 6}>
                          <FormFieldWrapper<T>
                            {...props}
                            name={`${props.name}[${index}].${key}`}
                            field={{ ...field, widthColumnCount: undefined }}
                            messagePlacement="top"
                            hideOptionalIndicator
                          />
                        </Grid>
                        {!field.readonly && field.descriptionText && (
                          <Grid
                            item
                            className={clsx(cssExports.formControlDescription, cssExports.verticalFormDescription)}
                          >
                            {typeof field.descriptionText === "string"
                              ? LocalizationHelperInstance.getFormattedMessage(
                                  field.descriptionText,
                                  field.descriptionText
                                )
                              : field.descriptionText}
                          </Grid>
                        )}
                      </>
                    ) : (
                      <></>
                    )
                  )}
                </Grid>
              </FormControlLayout>
            ))
          ) : (
            <></>
          );
        }}
      </Field>
    </>
  );
}

export default FormFieldSelector;
